// MediaPipe Face Mesh implementation for face detection and landmark drawing
// Uses CDN-loaded MediaPipe Face Mesh for reliable face detection

let faceMesh = null;
let isLoading = false;

// Initialize MediaPipe Face Mesh with performance optimizations
async function initializeFaceMesh() {
  if (faceMesh) return faceMesh;
  if (isLoading) {
    // Wait for existing initialization
    while (isLoading) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    return faceMesh;
  }

  isLoading = true;
  try {
    // Check if MediaPipe globals are available
    if (typeof window.FaceMesh === 'undefined') {
      throw new Error('MediaPipe Face Mesh not loaded. Please ensure CDN scripts are included.');
    }

    faceMesh = new window.FaceMesh({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
      }
    });

    // Optimized settings for faster performance
    faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: false, // Disable for faster processing
      minDetectionConfidence: 0.3, // Lower for faster detection
      minTrackingConfidence: 0.3,
      staticImageMode: true // Optimize for static images
    });

    return faceMesh;
  } catch (error) {
    throw new Error(`Failed to initialize MediaPipe Face Mesh: ${error.message}`);
  } finally {
    isLoading = false;
  }
}

// Detect face landmarks on an image element with performance optimizations
export async function detectFaceOnImageElement(imgEl, options = {}) {
  try {
    const mesh = await initializeFaceMesh();

    return new Promise((resolve, reject) => {
      // Set up one-time result handler with timeout cleanup
      let timeoutId;

      const onResults = (results) => {
        // Clear timeout and handler
        clearTimeout(timeoutId);
        mesh.onResults(() => {});

        if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
          resolve(results.multiFaceLandmarks[0]);
        } else {
          resolve(null);
        }
      };

      mesh.onResults(onResults);

      // Send image for processing
      mesh.send({ image: imgEl }).catch(error => {
        clearTimeout(timeoutId);
        mesh.onResults(() => {});
        reject(error);
      });

      // Shorter timeout for better UX - 5 seconds
      timeoutId = setTimeout(() => {
        mesh.onResults(() => {}); // Clear handler
        reject(new Error('Face detection timeout - please try again'));
      }, 5000);
    });
  } catch (error) {
    throw new Error(`Face detection failed: ${error.message}`);
  }
}

// Draw face landmarks with optimized mesh on canvas
export function drawLandmarksOnCanvas(landmarks, canvas, { color = "#FFFFFF", radius = 1.0 } = {}) {
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  if (!landmarks || landmarks.length === 0) return;

  // Performance optimization: batch drawing operations
  ctx.fillStyle = color;
  ctx.strokeStyle = color;

  // Draw face contour (most important visual element) - faster than full mesh
  if (typeof window.drawConnectors !== 'undefined' && typeof window.FACEMESH_FACE_OVAL !== 'undefined') {
    window.drawConnectors(ctx, landmarks, window.FACEMESH_FACE_OVAL, {
      color: color,
      lineWidth: 2
    });
  }

  // Draw simplified mesh - only key connections for performance
  if (typeof window.drawConnectors !== 'undefined' && typeof window.FACEMESH_TESSELATION !== 'undefined') {
    // Draw simplified mesh with thinner lines
    window.drawConnectors(ctx, landmarks, window.FACEMESH_TESSELATION, {
      color: color,
      lineWidth: 0.3 // Thinner for better performance
    });
  }

  // Draw key landmark points in batch
  ctx.beginPath();
  landmarks.forEach((landmark, index) => {
    // Only draw every 3rd landmark point for performance
    if (index % 3 === 0) {
      const x = landmark.x * canvas.width;
      const y = landmark.y * canvas.height;
      ctx.moveTo(x + radius, y);
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
    }
  });
  ctx.fill();
}

// Clean up resources
export function cleanup() {
  if (faceMesh) {
    try {
      faceMesh.close();
    } catch (e) {
      // Ignore cleanup errors
    }
    faceMesh = null;
  }
}
